import { STORE_WRAPPER_TOKEN } from '@actassa/api';
import { Inject, Injectable } from '@angular/core';
import { Dispatch } from '@ngxs-labs/dispatch-decorator';
import { Select } from '@ngxs/store';
import { isEmpty } from 'lodash-es';
import { merge, Observable, of } from 'rxjs';
import { filter, switchMap, map, concatAll, take } from 'rxjs/operators';
import { LoadJobsEvent } from '../+state/app-state/actions/load-jobs-event';
import { LoadPlacementsEvent } from '../+state/app-state/actions/load-placements-event';
import { LoadReportsEvent } from '../+state/app-state/actions/load-reports-event';
import { LoadShiftsEvent } from '../+state/app-state/actions/load-shifts-event';
import { JobsPlacementsState } from '../+state/app-state/app.state';
import { JobChangeStatusInterface } from '../interfaces/job-change-status.interface';
import { ReportInterface } from '../interfaces/report.interface';
import { ShiftChangeStatusInterface } from '../interfaces/shift-change-status.interface';
import { JobsService } from '../services/jobs.service';
import { ReportsService } from '../services/reports.service';
import { ShiftsService } from './shifts.service';

@Injectable({
    providedIn: 'root',
})
export class SchedulerService {
    @Select(JobsPlacementsState.awaitingJobStatusChanges$) public jobStatuses$: Observable<Array<any>>;
    @Select(JobsPlacementsState.awaitingShiftStatusChanges$) public shiftStatuses$: Observable<Array<any>>;
    @Select(JobsPlacementsState.awaitingShiftDataReports$) public shiftDataReports$: Observable<Array<any>>;

    constructor(
        @Inject(STORE_WRAPPER_TOKEN) private storeWrapper,
        private readonly jobsService: JobsService,
        private readonly shiftsService: ShiftsService,
        private readonly reportsService: ReportsService,
    ) {
        this.init().subscribe();
        this.preloadData();
    }

    public init(): Observable<void> {
        return merge(
            // interval(60000).pipe(tap(() => this.loadReports())),
            this.sendAwaitingShiftDataReports$(),
            this.sendAwaitingJobStatuses$(),
            this.sendAwaitingShiftStatuses$(),
        );
    }

    @Dispatch()
    public preloadData(): Array<LoadJobsEvent | LoadPlacementsEvent | LoadReportsEvent | LoadShiftsEvent> {
        return [
            new LoadJobsEvent(),
            new LoadShiftsEvent(),
            new LoadPlacementsEvent(),
            new LoadReportsEvent(),
        ];
    }

    private sendAwaitingJobStatuses$(): Observable<any> {
        return this.storeWrapper.isNetworkConnected$
            .pipe(
                filter((isConnected: boolean) => isConnected),
                switchMap(() => this.jobStatuses$.pipe(take(1))),
                filter(statuses => !isEmpty(statuses)),
                switchMap((statuses: Array<JobChangeStatusInterface>) => of(...statuses)),
                map((status: JobChangeStatusInterface) => this.jobsService.sendJobStatusToServer$(status)),
                concatAll(),
            );
    }

    private sendAwaitingShiftStatuses$(): Observable<any> {
        return this.storeWrapper.isNetworkConnected$
            .pipe(
                filter((isConnected: boolean) => isConnected),
                switchMap(() => this.shiftStatuses$.pipe(take(1))),
                filter(statuses => !isEmpty(statuses)),
                switchMap((statuses: Array<ShiftChangeStatusInterface>) => of(...statuses)),
                map((status: ShiftChangeStatusInterface) => this.shiftsService.sendShiftStatusToServer$(status)),
                concatAll(),
            );
    }

    private sendAwaitingShiftDataReports$(): Observable<any> {
        return this.storeWrapper.isNetworkConnected$
            .pipe(
                filter((isConnected: boolean) => isConnected),
                switchMap(() => this.shiftDataReports$.pipe(take(1))),
                filter(reports => !isEmpty(reports)),
                switchMap((reports: Array<ReportInterface>) => of(...reports)),
                map((report: ReportInterface) => this.reportsService.sendReportToServer$(report)),
                concatAll(),
            );
    }
}
